import type { ReactNode } from 'react';

/* ── SVGs padronizados ────────────────────────────────────────── */
const InfoSvg16 = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M8 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="4.5" r="0.75" fill="currentColor" />
  </svg>
);

const InfoSvg14 = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M7 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="7" cy="4" r="0.5" fill="currentColor" />
  </svg>
);

/* ── Sub-componentes ──────────────────────────────────────────── */

interface HeaderProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
}

export function TooltipHeader({ icon, title, subtitle }: HeaderProps) {
  return (
    <div className="flex items-start gap-3 p-4 pb-2">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
        {icon}
      </div>
      <div className="flex-1">
        <h4 className="text-[0.95rem] font-semibold leading-tight text-gray-900 dark:text-gray-100">
          {title}
        </h4>
        <p className="mt-0.5 text-[0.8rem] leading-snug text-gray-500 dark:text-gray-400">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

export function TooltipDetail({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-[0.8rem] leading-snug text-gray-500 dark:text-gray-400">
      <span className="mt-px flex-shrink-0 text-blue-600 dark:text-blue-400">
        {InfoSvg14}
      </span>
      <span className="flex-1 [&>strong]:font-semibold [&>strong]:text-gray-900 dark:[&>strong]:text-gray-100">
        {children}
      </span>
    </div>
  );
}

export function TooltipBody({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-2 px-4 pb-3">
      {children}
    </div>
  );
}

export function TooltipTip({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 border-t border-gray-200 bg-blue-50 px-4 py-3 dark:border-gray-700 dark:bg-blue-900/20">
      <span className="flex-shrink-0 text-sm">💡</span>
      <p className="text-[0.8rem] leading-snug text-blue-700 dark:text-blue-300 [&>strong]:font-semibold">
        {children}
      </p>
    </div>
  );
}

/* ── Componente principal ─────────────────────────────────────── */

interface Props {
  children: ReactNode;
}

export function InfoTooltip({ children }: Props) {
  return (
    <span className="group/tip relative inline-flex cursor-help leading-[1]">
      {/* Trigger icon */}
      <span className="inline-flex items-center justify-center rounded-full p-0.5 text-gray-400 transition-all group-hover/tip:scale-110 group-hover/tip:bg-blue-50 group-hover/tip:text-blue-600 dark:group-hover/tip:bg-blue-900/30 dark:group-hover/tip:text-blue-400">
        {InfoSvg16}
      </span>

      {/* Tooltip card */}
      <span className="pointer-events-none absolute left-1/2 top-full z-[1000] mt-2 w-[340px] -translate-x-1/2 overflow-hidden rounded-xl border border-gray-200 bg-white opacity-0 shadow-[0_4px_20px_rgba(0,0,0,0.15)] transition-all group-hover/tip:pointer-events-auto group-hover/tip:opacity-100 dark:border-gray-700 dark:bg-gray-900">
        {/* Seta dupla (borda + fundo) */}
        <span className="absolute -top-[8px] left-1/2 -translate-x-1/2 border-x-8 border-b-8 border-x-transparent border-b-gray-200 dark:border-b-gray-700" />
        <span className="absolute -top-[6px] left-1/2 -translate-x-1/2 border-x-[6px] border-b-[6px] border-x-transparent border-b-white dark:border-b-gray-900" />
        {children}
      </span>
    </span>
  );
}
